import { useCallback, useEffect, useState } from 'react';
import { useAsk } from '../components/Ask';
import { api, ApiError } from '../lib/api';
import { formatMinutes } from '../lib/types';
import type { Priority } from '../lib/types';

interface Row {
  priority: Priority;
  /** Which of the two is actually in force — their own agreement, or the platform default. */
  source: 'agreed' | 'default' | 'none';
  responseMinutes: number | null;
  resolveMinutes: number | null;
  defaultResponseMinutes: number | null;
  defaultResolveMinutes: number | null;
}

interface Doc {
  mayChange: boolean;
  priorities: Row[];
}

const PRIORITY_LABELS: Record<Priority, string> = {
  critical: 'Critical',
  high: 'High',
  medium: 'Medium',
  low: 'Low',
  none: 'None',
};

/**
 * What we have promised this customer, per priority.
 *
 * `sla_policies` sat in the schema from the SLA work with `policyFor` already written to take a
 * company's own row and fall back to a platform default — and nothing anywhere to write one. So
 * the clocks ran, the target and breach badges appeared on every list, and every customer was on
 * the same five defaults whatever had actually been agreed with them. This is the screen that
 * makes an agreement possible.
 *
 * READ-ONLY FOR THE CUSTOMER RATHER THAN HIDDEN, which is where this parts company with the
 * rates card above it. A rate is what we charge and is not theirs to see; an SLA is a promise
 * MADE TO THEM, and it is what the target badge on their own tasks is calculated from. Somebody
 * who can see "due in four hours" should be able to find out why it says four.
 *
 * CHANGING ONE NEVER MOVES WORK ALREADY RUNNING. The target is worked out once, when a task is
 * raised, and stored on it — so a new agreement applies to what comes in next and a customer
 * never watches a promise they were already given move. Said on the card, because it is the
 * first thing somebody wonders when they change a number here.
 */
export function SlaCard({ companyId }: { companyId: string }) {
  const [doc, setDoc] = useState<Doc | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<Priority | null>(null);
  const ask = useAsk();

  const load = useCallback(async () => {
    try {
      setDoc(await api.get<Doc>(`/companies/${companyId}/sla`));
      setError(null);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not read what we have promised');
      setDoc(null);
    }
  }, [companyId]);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Asked in HOURS, stored in minutes.
   *
   * Nobody agrees a response target in minutes — it is "four hours", "one working day". Minutes
   * are what the clock needs and what the column is, and making somebody multiply by sixty to
   * use the screen is making them do the computer's job. Fractions are allowed through for the
   * half-hour case and rounded, because a target of 29.6 minutes is not a thing anybody meant.
   */
  async function edit(row: Row) {
    if (busy) return;

    const asHours = (minutes: number | null) => (minutes === null ? '' : String(minutes / 60));

    const reply = await ask.prompt({
      title: `${PRIORITY_LABELS[row.priority]} — how long until somebody replies?`,
      body:
        'In working hours: the clock only runs Monday to Thursday 08:00–16:30 and Friday 08:00–16:00, ' +
        'not on public holidays, so four hours agreed on a Friday afternoon lands on Monday morning.',
      label: 'Hours until a first reply',
      placeholder: '4',
      initial: asHours(row.responseMinutes),
      confirmLabel: 'Next',
    });
    if (reply === null) return;

    const fix = await ask.prompt({
      title: `${PRIORITY_LABELS[row.priority]} — how long until it is fixed?`,
      body: 'Also in working hours, and it cannot be sooner than the reply.',
      label: 'Hours until resolved',
      placeholder: '24',
      initial: asHours(row.resolveMinutes),
      confirmLabel: 'Agree it',
    });
    if (fix === null) return;

    const responseMinutes = Math.round(Number(reply) * 60);
    const resolveMinutes = Math.round(Number(fix) * 60);

    /* Checked here as well as on the server, so a typo says so without a round trip. */
    if (!Number.isFinite(responseMinutes) || !Number.isFinite(resolveMinutes) || responseMinutes < 1 || resolveMinutes < 1) {
      setError('Both targets have to be a number of hours, and at least a minute');
      return;
    }
    if (resolveMinutes < responseMinutes) {
      setError('A fix cannot be due before the reply to it is');
      return;
    }

    setBusy(row.priority);
    setError(null);
    try {
      await api.put(`/companies/${companyId}/sla/${row.priority}`, { responseMinutes, resolveMinutes });
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not save that');
    } finally {
      setBusy(null);
    }
  }

  async function drop(row: Row) {
    if (busy) return;

    const back =
      row.defaultResponseMinutes !== null && row.defaultResolveMinutes !== null
        ? `${formatMinutes(row.defaultResponseMinutes)} to reply and ${formatMinutes(row.defaultResolveMinutes)} to fix`
        : 'no target at all';

    if (
      !(await ask.confirm({
        title: `Drop the ${PRIORITY_LABELS[row.priority].toLowerCase()} agreement?`,
        body:
          `They go back to the standard promise — ${back}. Work already raised keeps the target it ` +
          'was given; this only changes what the next one gets.',
        confirmLabel: 'Drop it',
        danger: true,
      }))
    ) {
      return;
    }

    setBusy(row.priority);
    setError(null);
    try {
      await api.del(`/companies/${companyId}/sla/${row.priority}`);
      await load();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Could not drop that');
    } finally {
      setBusy(null);
    }
  }

  if (!doc) {
    return (
      <div className="card">
        <div className="card-head">
          <h3>What we have promised</h3>
        </div>
        <div style={{ padding: '0 14px 14px' }}>
          {error ? <div className="callout callout-bad">{error}</div> : <span className="dim">Reading…</span>}
        </div>
      </div>
    );
  }

  return (
    <div className="card">
      <div className="card-head">
        <h3>What we have promised</h3>
        <span className="dim" style={{ fontSize: 12 }}>
          {doc.mayChange ? 'Response and resolve targets, in working hours' : 'Your service level agreement'}
        </span>
      </div>

      {error && (
        <div style={{ padding: '0 14px' }}>
          <div className="callout callout-bad">{error}</div>
        </div>
      )}

      <table className="data">
        <thead>
          <tr>
            <th>Priority</th>
            <th className="right">Reply within</th>
            <th className="right">Fixed within</th>
            <th>Agreement</th>
            {doc.mayChange && <th />}
          </tr>
        </thead>
        <tbody>
          {doc.priorities.map((row) => (
            <tr key={row.priority}>
              <td>{PRIORITY_LABELS[row.priority]}</td>
              <td className="right mono">
                {row.responseMinutes === null ? <span className="dim">—</span> : formatMinutes(row.responseMinutes)}
              </td>
              <td className="right mono">
                {row.resolveMinutes === null ? <span className="dim">—</span> : formatMinutes(row.resolveMinutes)}
              </td>
              <td>
                {/*
                  Three states, and the difference matters: what was agreed with THEM, what every
                  customer gets, and nothing at all — which means a task at this priority starts
                  with no clock on it, and is worth seeing rather than inferring from two dashes.
                */}
                {row.source === 'agreed' ? (
                  <span className="badge badge-accent">Theirs</span>
                ) : row.source === 'default' ? (
                  <span className="dim">Standard</span>
                ) : (
                  <span className="dim">No target</span>
                )}
              </td>
              {doc.mayChange && (
                <td className="right">
                  <div className="row" style={{ justifyContent: 'flex-end', gap: 5 }}>
                    <button
                      type="button"
                      className="btn btn-quiet btn-sm"
                      disabled={busy !== null}
                      onClick={() => void edit(row)}
                    >
                      {busy === row.priority ? 'Saving…' : row.source === 'agreed' ? 'Change' : 'Agree one'}
                    </button>
                    {row.source === 'agreed' && (
                      <button
                        type="button"
                        className="btn btn-quiet btn-sm"
                        disabled={busy !== null}
                        title="Put them back on the standard promise"
                        onClick={() => void drop(row)}
                      >
                        Drop
                      </button>
                    )}
                  </div>
                </td>
              )}
            </tr>
          ))}
        </tbody>
      </table>

      <div style={{ padding: '10px 14px 14px' }}>
        <p className="dim" style={{ margin: 0, fontSize: 12 }}>
          {doc.mayChange
            ? 'Targets run in our working hours — Monday to Thursday 08:00–16:30, Friday 08:00–16:00, not on public holidays — so they pause overnight and at weekends. Changing one never moves work already raised — a task keeps the target it was given on the day it came in.'
            : 'Targets run in working hours — Monday to Thursday 08:00–16:30, Friday 08:00–16:00, not on public holidays — so they pause overnight and at weekends. A task keeps the target it was given on the day it was raised.'}
        </p>
      </div>
    </div>
  );
}
